import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import SimcInput from 'shared/simcinput';
import { className as getClassName, activeTalents, getCharacterSpecInfo, getFaction } from 'shared/character';
import { getWeaponHands, enchants } from 'shared/items';
import { getTalent } from 'shared/talents';
import { TALENT_RINGS } from 'shared/specialItems';
import { getSelectedGear, getCombinations } from 'shared/combinations';
import Droptimizer from 'shared/droptimizer';

import jobs from './jobs';
import archivedJobs from './archivedJobs';
import realms from './realms';

import form, { selectors as formSelectors } from './form';
import gearsets, { selectors as gearsetsSelectors } from './gearsets';
import character, { selectors as characterSelectors } from './character';
import savedGearsets from './savedGearsets';
import selectedSavedGearset from './selectedSavedGearset';
import simcItems, { selectors as simcItemsSelectors } from './simcItems';
// need to figure out how to clean up expansion option references without cycles
// eslint-disable-next-line import/no-cycle
import simcOptions, { selectors as simcOptionsSelectors, expansionOptionConfig } from './simcOptions';
import talentSets, { selectors as talentSetsSelectors } from './talentSets';
import optimize, { selectors as optimizeSelectors } from './optimize';
import droptimizer, { selectors as droptimizerSelectors } from './droptimizer';
import { selectors as gemEnchantSelectors } from './optimize/gemEnchant';
import { selectors as enhancementSelectors } from './optimize/enhancements';
import { selectors as essenceSelectors } from './optimize/azeriteEssences';
import { selectors as covenantSelectors } from './optimize/covenants';
import customApls, { selectors as customAplsSelectors } from './customApls';
import report from './report';
import reportOptions from './reportOptions';
import simcExpertOptions, { selectors as simcExpertSelectors } from './simcExpertOptions';

export default combineReducers({
  archivedJobs,
  character,
  customApls,
  droptimizer,
  form,
  gearsets,
  jobs,
  optimize,
  realms,
  report,
  reportOptions,
  savedGearsets,
  selectedSavedGearset,
  simcExpertOptions,
  simcItems,
  simcOptions,
  talentSets,
});

const equippedGearSelector = createSelector(
  characterSelectors.data,
  simcItemsSelectors.items,
  (char, itemsFromSimc) => {
    if (char && char.items) {
      return _.pickBy(
        char.items,
        _.isObject,
      );
    }
    if (itemsFromSimc) {
      return itemsFromSimc;
    }
    return {};
  },
);

const characterSelector = createSelector(
  characterSelectors.data,
  formSelectors.text,
  optimizeSelectors.data,
  (state, props) => _.get(props, 'type', null),
  (char, simcInput, optimizeData, simType) => {
    if (simType === 'optimize') {
      return SimcInput.toCharacter(optimizeData);
    }

    if (char) {
      return char;
    }

    return SimcInput.toCharacter(SimcInput.parse(simcInput));
  },
);


const characterSpecInfoSelector = createSelector(
  characterSelector,
  char => getCharacterSpecInfo(char),
);

const characterClassNameSelector = createSelector(
  characterSelector,
  char => getClassName(char) || '',
);

const characterClassIdSelector = createSelector(
  characterSpecInfoSelector,
  specInfo => _.get(specInfo, 'class.id'),
);

const characterSpecIdSelector = createSelector(
  characterSpecInfoSelector,
  specInfo => _.get(specInfo, 'id'),
);

const characterSpecSelector = createSelector(
  characterSelector,
  char => _.get(activeTalents(char), 'spec.name', ''),
);

const characterFactionSelector = createSelector(
  characterSelector,
  char => getFaction(char),
);

const characterCovenantSelector = createSelector(
  characterSelector,
  char => {
    return char?.covenant?.key;
  },
);

const azeriteLevelSelector = createSelector(
  characterSelector,
  char => {
    return _.get(char, 'items.neck.azeriteItem.azeriteLevel', 0);
  },
);

const artifactTraitsSelector = createSelector(
  characterSelector,
  char => {
    const armoryTraits = _.sortBy(
      _.get(char, 'items.mainHand.artifactTraits') || _.get(char, 'items.offHand.artifactTraits') || [],
      'id',
    );
    return armoryTraits;
  },
);

// if simc option is -1, search simc input comments for a value from the addon
const reoriginationArraySelector = createSelector(
  simcOptionsSelectors.reoriginationArray,
  formSelectors.text,
  optimizeSelectors.data,
  (state, props) => _.get(props, 'type', null),
  (reoriginationArray, simcInput, optimizeData, simType) => {
    if (parseInt(reoriginationArray) === -1) {
      const searchData = simType === 'optimize' ? optimizeData : SimcInput.parse(simcInput);
      const ro = SimcInput.find(searchData, 'bfa.reorigination_array_stacks', true);
      return ro !== null ? ro : -1;
    }
    return reoriginationArray;
  },
);

const talentRingOverrideNameSelector = createSelector(
  characterSelector,
  characterSpecSelector,
  optimizeSelectors.talentRingOverride,
  (char, spec, talentRingOverride) => {
    if (char && spec) {
      const specName = _.snakeCase(spec);
      const talentRingInfo = _.find(TALENT_RINGS, { classId: char.class, spec: specName });
      if (talentRingInfo && talentRingOverride !== null) {
        const talent = getTalent(char.class, spec, talentRingInfo.row, talentRingOverride + 1);
        return _.snakeCase(_.get(talent, 'spell.name', ''));
      }
    }
    return null;
  },
);

const aplSelector = createSelector(
  characterSelector,
  characterClassNameSelector,
  characterSpecSelector,
  customAplsSelectors.customApls,
  (char, className, spec, apls) => _.get(_.find(apls, apl => {
    const aplClassName = _.get(apl, 'className', '').toLowerCase();
    const aplSpec = _.get(apl, 'spec', '').toLowerCase();
    const found = className &&
      spec &&
      className.toLowerCase() === aplClassName &&
      spec.toLowerCase() === aplSpec;
    return found;
  }), 'apl', ''),
);

const aplDataSelector = createSelector(
  aplSelector,
  rawApl => {
    const aplData = SimcInput.parse(rawApl);
    return aplData;
  },
);

const aplInvalidCommandsSelector = createSelector(
  aplDataSelector,
  aplData => {
    const invalidCommands = _.map(SimcInput.getInvalidAplCommands(aplData), item => item.key);
    return invalidCommands;
  },
);

const inputInCustomAplWarningSelector = createSelector(
  aplInvalidCommandsSelector,
  invalidCommands => {
    const hasInputOptions = _.size(
      _.intersection(
        ['level', 'race', 'spec', 'talents'],
        invalidCommands,
      ),
    ) > 0;
    return hasInputOptions;
  },
);

const optimizeGemSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.gem,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);

const optimizeCloakEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.cloakEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeNeckEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.neckEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeRingEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.ringEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeWeaponEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.weaponEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeWeaponOffhandEnchantSelector = createSelector(
  characterClassNameSelector,
  characterSpecSelector,
  gemEnchantSelectors.weaponOffhandEnchant,
  (className, spec, data) => _.get(_.find(data, { className, spec }), 'value', ''),
);
const optimizeSelectedEnhancementsSelector = createSelector(
  characterSpecInfoSelector,
  enhancementSelectors.selectedEnhancements,
  (specInfo, selectedEnhancements) => {
    if (specInfo) {
      return _.filter(selectedEnhancements, { specId: specInfo.id });
    }
    return null;
  },
);
const optimizeForceMainStatSelector = createSelector(
  characterSpecInfoSelector,
  enhancementSelectors.forceMainStat,
  (specInfo, forceMainStat) => {
    if (specInfo) {
      return _.get(_.find(forceMainStat, { specId: specInfo.id }), 'value');
    }
    return null;
  },
);
const addSocketGemSelector = createSelector(
  characterSpecInfoSelector,
  enhancementSelectors.addSocketGem,
  (specInfo, addSocketGem) => {
    if (specInfo) {
      const specResult = _.find(addSocketGem, { specId: specInfo.id });
      const gemId = _.get(specResult, 'gemId', '');
      return gemId;
    }
    return null;
  },
);

const selectedGearSelector = createSelector(
  optimizeSelectors.gear,
  optimizeSelectors.selected,
  optimizeSelectors.selectedAzeritePowers,
  getSelectedGear,
);
const advancedIterationsSelector = createSelector(
  formSelectors.advancedData,
  simcOptionsSelectors.iterations,
  (data, iterationsOverride) => SimcInput.getTotalIterations(data, { defaultIterations: iterationsOverride }),
);

const enhancementSlotMap = {
  rings: 'finger',
};
const optimizeMissingEnchantsSelector = createSelector(
  // get the versions of this data from optimize, not the more general ones
  optimizeSelectors.equippedGear,
  optimizeSelectors.selectedGear,
  optimizeSelectedEnhancementsSelector,
  (equippedGear, selectedGear, enhancements) => {
    const slotsMissingEnchants = new Set();

    const enchantSlots = ['main_hand', 'off_hand', 'chest', 'feet', 'rings', 'hands'];
    _.each(selectedGear, (items, slot) => {
      if (_.includes(enchantSlots, slot)) {
        const mixedEnchants = _.some(items, 'enchant_id') && !_.every(items, 'enchant_id');
        if (mixedEnchants) {
          // check enhancements to see if there is a selection for this slot, ignore zero DPS enchants
          const hasEnchantSelected = _.find(enhancements, enh => {
            return (enh.slot === slot || enh.slot === enhancementSlotMap[slot]) && !enh.zeroDps;
          });
          if (!hasEnchantSelected) {
            // check existing enchants to see if they are zero DPS enchants
            const hasDpsEnchants = _.some(items, item => {
              if (item.enchant_id) {
                const enchant = _.find(enchants, { id: parseInt(item.enchant_id, 10) });
                if (enchant) {
                  return !enchant.zeroDps;
                }
              }
              return false;
            });
            if (hasDpsEnchants) {
              slotsMissingEnchants.add(slot);
            }
          }
        }
      }
    });

    return [...slotsMissingEnchants];
  },
);

const defaultReportNameSelector = createSelector(
  (state, props) => props.type,
  formSelectors.defaultReportName,
  droptimizerSelectors.reportName,
  (type, defaultReportName, droptimizerReportName) => {
    if (type === 'droptimizer') {
      return droptimizerReportName;
    }
    return defaultReportName;
  },
);

const reportNameSelector = createSelector(
  formSelectors.userReportName,
  defaultReportNameSelector,
  (userReportName, defaultReportName) => {
    return userReportName || defaultReportName;
  },
);

const mainHandAlternatesSelector = createSelector(
  simcItemsSelectors.bags,
  (bags) => {
    const mainHandWeapons = _.reverse(
      _.clone(_.sortBy(_.filter(bags, { slot: 'main_hand' }), ['itemLevel', 'id'])),
    );
    const oneHanders = _.filter(mainHandWeapons, item => getWeaponHands(item) !== '2h');
    const valid = _.filter(oneHanders, item => item.itemLevel > 10);
    return valid;
  },
);
const mainHandAlternateWeaponSelector = createSelector(
  mainHandAlternatesSelector,
  droptimizerSelectors.mainHandAlternate,
  (alternates, mainHandAlternateIdx) => {
    // grab first item if the index doesn't exist
    // might happen because i'm changing the behavior because it wasn't properly filtering
    return alternates[mainHandAlternateIdx] || alternates[0];
  },
);
const offHandAlternatesSelector = createSelector(
  simcItemsSelectors.bags,
  (bags) => {
    const offHandWeapons = _.reverse(
      _.clone(_.sortBy(_.filter(bags, { slot: 'off_hand' }), ['itemLevel', 'id'])),
    );
    const valid = _.filter(offHandWeapons, item => item.itemLevel > 10);
    return valid;
  },
);
const offHandAlternateWeaponSelector = createSelector(
  offHandAlternatesSelector,
  droptimizerSelectors.offHandAlternate,
  (alternates, offHandAlternateIdx) => {
    return alternates[offHandAlternateIdx] || alternates[0];
  },
);

const characterLootSpecSelector = createSelector(
  characterClassIdSelector,
  characterSpecIdSelector,
  droptimizerSelectors.lootSpec,
  (classId, specId, lootSpec) => {
    return _.get(lootSpec, `[${classId}][${specId}]`) || specId;
  },
);

const droptimizerOptionsSelector = (state, props) => ({
  equipped: equippedGearSelector(state, props),
  mainHandAlternateWeapon: mainHandAlternateWeaponSelector(state, props),
  offHandAlternateWeapon: offHandAlternateWeaponSelector(state, props),
  instance: droptimizerSelectors.selectedInstance(state, props),
  encounter: droptimizerSelectors.selectedEncounter(state, props),
  difficulty: droptimizerSelectors.selectedDifficulty(state, props),
  warforgeLevel: droptimizerSelectors.selectedWarforgeLevel(state, props),
  gem: droptimizerSelectors.gem(state, props),
  azeriteLevel: azeriteLevelSelector(state, props),
  allAzeritePowers: droptimizerSelectors.allAzeritePowers(state, props),
  classId: characterClassIdSelector(state, props),
  specId: characterSpecIdSelector(state, props),
  lootSpecId: characterLootSpecSelector(state, props),
  faction: characterFactionSelector(state, props),
  covenant: characterCovenantSelector(state, props),
});

const combinationsSelector = createSelector(
  optimizeSelectors.rawCombinations,
  optimizeSelectors.requireLegendaries,
  optimizeSelectors.tier21Pieces,
  optimizeSelectors.tier20Pieces,
  optimizeSelectors.tier19Pieces,
  optimizeSelectors.lockedRing,
  optimizeSelectors.lockedTrinket,
  optimizeSelectors.classId,
  optimizeSelectors.spec,
  enhancementSelectors.selectedEnhancements,
  optimizeSelectors.enhancementReplace,
  enhancementSelectors.forceMainStat,
  optimizeSelectors.equippedGear,
  optimizeSelectors.equippedTalents,
  optimizeSelectors.equippedEssences,
  optimizeSelectors.equippedSoulbind,
  simcOptionsSelectors.enableRuneWords,
  optimizeSelectors.renown,
  getCombinations,
);
const numCombinationsSelector = createSelector(
  optimizeSelectors.rawCombinations,
  combinationsSelector,
  (rawCombos, combosResult) => {
    const combos = combosResult.combinations;
    if (_.isArray(combos)) {
      return combos.length;
    }
    return rawCombos.length;
  },
);

const droptimizerItemsSelector = (state, props) => {
  if (Droptimizer.ready()) {
    return Droptimizer.getVariations(droptimizerOptionsSelector(state, props));
  }

  return null;
};

const formDataSelector = (state, props) => {
  const data = {
    type: props.type,
    text: formSelectors.text(state, props),
    advancedInput: formSelectors.advancedInput(state, props),

    baseActorName: formSelectors.baseActorName(state, props) || formSelectors.defaultBaseActorName(state, props),
    reportName: reportNameSelector(state, props),

    armory: {
      region: formSelectors.armoryRegion(state, props),
      realm: formSelectors.armoryRealm(state, props),
      name: formSelectors.armoryName(state, props),
    },

    email: formSelectors.email(state, props),
    sendEmail: formSelectors.sendEmail(state, props),

    character: characterSelectors.data(state, props),
    spec: characterSpecSelector(state, props),

    simcItems: simcItemsSelectors.items(state, props),

    gearsets: gearsetsSelectors.gearsets(state, props),

    talents: characterSelectors.talents(state, props),
    talentSets: talentSetsSelectors.talentSets(state, props),

    optimize: {
      data: optimizeSelectors.data(state, props),
      gear: selectedGearSelector(state, props),
      allGear: optimizeSelectors.gear(state, props),
      requireLegendaries: optimizeSelectors.requireLegendaries(state, props),
      tier21Pieces: optimizeSelectors.tier21Pieces(state, props),
      tier20Pieces: optimizeSelectors.tier20Pieces(state, props),
      tier19Pieces: optimizeSelectors.tier19Pieces(state, props),
      numCombinations: numCombinationsSelector(state, props),
      gem: optimizeGemSelector(state, props),
      ringEnchant: optimizeRingEnchantSelector(state, props),
      cloakEnchant: optimizeCloakEnchantSelector(state, props),
      neckEnchant: optimizeNeckEnchantSelector(state, props),
      weaponEnchant: optimizeWeaponEnchantSelector(state, props),
      weaponOffhandEnchant: optimizeWeaponOffhandEnchantSelector(state, props),
      replaceExisting: gemEnchantSelectors.replaceExisting(state, props),
      useMainStatGem: gemEnchantSelectors.useMainStatGem(state, props),
      source: optimizeSelectors.source(state, props),

      lockedRing: optimizeSelectors.lockedRing(state, props),
      lockedTrinket: optimizeSelectors.lockedTrinket(state, props),

      talentRingOverride: optimizeSelectors.talentRingOverride(state, props),
      talentRingOverrideName: talentRingOverrideNameSelector(state, props),

      selectedTalents: optimizeSelectors.selectedTalents(state, props),
      lockedTalents: optimizeSelectors.lockedTalents(state, props),

      selectedEnhancements: enhancementSelectors.selectedEnhancements(state, props),
      enhancementReplace: optimizeSelectors.enhancementReplace(state, props),
      forceMainStat: enhancementSelectors.forceMainStat(state, props),
      addSocketNum: enhancementSelectors.addSocketNum(state, props),
      addSocketGem: enhancementSelectors.addSocketGem(state, props),

      selectedEssences: optimizeSelectors.selectedEssences(state, props),
      passiveEssences: essenceSelectors.passives(state, props),

      equippedGear: optimizeSelectors.equippedGear(state, props),
      equippedTalents: optimizeSelectors.equippedTalents(state, props),
      equippedEssences: optimizeSelectors.equippedEssences(state, props),
      equippedSoulbind: optimizeSelectors.equippedSoulbind(state, props),

      // send these along so they get saved in the report for reloading / run sim again
      selected: optimizeSelectors.selected(state, props),
      selectedAzeritePowers: optimizeSelectors.selectedAzeritePowers(state, props),
      azeriteEssencesAvailable: essenceSelectors.available(state, props),
      selectedEssencesRaw: essenceSelectors.selected(state, props),
      lockedMinor: essenceSelectors.lockedMinor(state, props),
      lockedMinors: essenceSelectors.lockedMinors(state, props),

      // Shadowlands stuff
      soulbinds: covenantSelectors.soulbinds(state, props),
      soulbindGroups: covenantSelectors.soulbindGroups(state, props),
      conduits: covenantSelectors.conduits(state, props),
      conduitRankOverride: covenantSelectors.conduitRankOverride(state, props),
      allSoulbinds: covenantSelectors.allSoulbinds(state, props),
      allConduits: covenantSelectors.allConduits(state, props),
      allCovenants: covenantSelectors.allCovenants(state, props),
      unlockedSoulbindTraits: optimizeSelectors.unlockedSoulbindTraits(state, props),
      renown: optimizeSelectors.renown(state, props),
      soulbindsModified: covenantSelectors.soulbindsModified(state, props),
    },

    droptimizer: droptimizerOptionsSelector(state, props),
    droptimizerItems: droptimizerItemsSelector(state, props),

    simcVersion: simcOptionsSelectors.simcVersion(state, props),
    iterations: simcOptionsSelectors.iterations(state, props),
    smartHighPrecision: simcOptionsSelectors.smartHighPrecision(state, props),
    fightStyle: simcOptionsSelectors.fightStyle(state, props),
    fightLength: simcOptionsSelectors.fightLength(state, props),
    enemyCount: simcOptionsSelectors.enemyCount(state, props),
    enemyType: simcOptionsSelectors.enemyType(state, props),
    potion: simcOptionsSelectors.potion(state, props),
    food: simcOptionsSelectors.food(state, props),
    flask: simcOptionsSelectors.flask(state, props),
    augmentation: simcOptionsSelectors.augmentation(state, props),

    bloodlust: simcOptionsSelectors.bloodlust(state, props),
    arcaneIntellect: simcOptionsSelectors.arcaneIntellect(state, props),
    fortitude: simcOptionsSelectors.fortitude(state, props),
    battleShout: simcOptionsSelectors.battleShout(state, props),
    mysticTouch: simcOptionsSelectors.mysticTouch(state, props),
    chaosBrand: simcOptionsSelectors.chaosBrand(state, props),
    bleeding: simcOptionsSelectors.bleeding(state, props),
    windfury: simcOptionsSelectors.windfury(state, props),
    reoriginationArray: reoriginationArraySelector(state, props),

    reportDetails: simcOptionsSelectors.reportDetails(state, props),
    pantheonTrinkets: simcOptionsSelectors.pantheonTrinkets(state, props),

    zuldazar: simcOptionsSelectors.zuldazar(state),

    apl: aplSelector(state, props),

    expertHeader: simcExpertSelectors.headerInput(state, props),
    expertPreActors: simcExpertSelectors.preActorsInput(state, props),
    expertPostActors: simcExpertSelectors.postActorsInput(state, props),
    expertFooter: simcExpertSelectors.footerInput(state, props),

    ptr: simcOptionsSelectors.ptr(state, props),

    frontendHost: window.location.host, // eslint-disable-line
    frontendVersion: process.env.VERSION,
  };

  _.each(expansionOptionConfig, option => {
    if (data[option.key]) {
      throw new Error(`formData selector already has property ${option.key} - you screwed up an expansion option`);
    }
    data[option.key] = simcOptionsSelectors[option.key](state, props);
  });

  return data;
};

export const selectors = {
  equippedGear: equippedGearSelector,
  apl: aplSelector,
  aplData: aplDataSelector,
  aplInvalidCommands: aplInvalidCommandsSelector,
  inputInCustomAplWarning: inputInCustomAplWarningSelector,
  character: characterSelector,
  characterClassName: characterClassNameSelector,
  characterSpec: characterSpecSelector,
  characterSpecInfo: characterSpecInfoSelector,
  characterFaction: characterFactionSelector,
  characterCovenant: characterCovenantSelector,
  mainHandAlternates: mainHandAlternatesSelector,
  offHandAlternates: offHandAlternatesSelector,
  mainHandAlternateWeapon: mainHandAlternateWeaponSelector,
  offHandAlternateWeapon: offHandAlternateWeaponSelector,
  characterLootSpec: characterLootSpecSelector,
  artifactTraits: artifactTraitsSelector,
  azeriteLevel: azeriteLevelSelector,
  optimizeGem: optimizeGemSelector,
  optimizeCloakEnchant: optimizeCloakEnchantSelector,
  optimizeNeckEnchant: optimizeNeckEnchantSelector,
  optimizeRingEnchant: optimizeRingEnchantSelector,
  optimizeWeaponEnchant: optimizeWeaponEnchantSelector,
  optimizeWeaponOffhandEnchant: optimizeWeaponOffhandEnchantSelector,
  optimizeSelectedEnhancements: optimizeSelectedEnhancementsSelector,
  optimizeForceMainStat: optimizeForceMainStatSelector,
  optimizeMissingEnchants: optimizeMissingEnchantsSelector,
  addSocketGem: addSocketGemSelector,
  advancedIterations: advancedIterationsSelector,
  formData: formDataSelector,
  reportName: reportNameSelector,
  defaultReportName: defaultReportNameSelector,
  combinations: combinationsSelector,
  numCombinations: numCombinationsSelector,
};
